/**
 * Captures a run's *replayable record* as the player plays — the seed, the
 * viewport, the player's input timeline and the mode — producing a
 * {@link RunRecord} (a few KB) at game-over.
 *
 * That record is the keystone of "one sim, three jobs"
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md): re-running the deterministic core sim
 * (`core/world.ts` → `runSimulation`) over the SAME seed + inputs reproduces the
 * run byte-for-byte. That single property powers:
 *  - **anti-cheat** — a Cloud Function re-runs the record to compute the
 *    authoritative score (the client's number is never trusted), and
 *  - **replays** (POPSTAR LIVE, later) — the record plays back the run.
 *
 * The recorder is a *capture buffer only*. It performs no simulation and reads no
 * wall-clock time: the scene feeds it the sim's `elapsedMs` at each sampled step
 * (the same clock the core sim advances), so the timeline lines up exactly with
 * what `runSimulation` will replay. It deliberately holds NO Phaser handles — it
 * takes plain numbers — keeping it trivially unit-testable and engine-agnostic.
 *
 * Sampling is *change-gated*: a sample is stored only when the pointer target has
 * moved more than a small threshold since the last stored sample (the sim eases
 * toward the last target, so a held position needs no repeats). This keeps the
 * record tiny on quiet stretches while staying exact where the player is active.
 */
import type { GameMode, RunInputSample, RunRecord, Viewport } from '../core';
import { SIM_VERSION } from '../core';

/** Squared pixel distance below which a new pointer target isn't worth recording. */
const MIN_SAMPLE_MOVE_SQ = 1.5 * 1.5;

export class RunRecorder {
  private readonly seed: number;
  private readonly viewport: Viewport;
  private readonly mode: GameMode;

  private readonly samples: RunInputSample[] = [];
  private lastX = Number.NaN;
  private lastY = Number.NaN;

  /**
   * @param seed     the run seed (the SAME value used to build the world; the
   *                 server rebuilds the world from it to validate the run).
   * @param viewport the play area the run is being played in (px). Recorded so a
   *                 replay re-simulates at the exact resolution the inputs assume.
   * @param mode     solo or multiplayer (multiplayer shares one seed across players).
   */
  constructor(seed: number, viewport: Viewport, mode: GameMode) {
    this.seed = seed;
    this.viewport = viewport;
    this.mode = mode;
  }

  /**
   * Record the player's pointer target for the step at `tMs` (the sim's
   * `elapsedMs`, NOT wall-clock). Call once per fixed step with the resolved
   * target. Stored only if it moved meaningfully since the last sample; the first
   * sample is always kept so the timeline has a known starting point.
   *
   * @param tMs the sim time of this step (ms) — must be non-decreasing.
   * @param x   pointer target x for this step (viewport px).
   * @param y   pointer target y for this step (viewport px).
   */
  sample(tMs: number, x: number, y: number): void {
    if (this.samples.length > 0) {
      const dx = x - this.lastX;
      const dy = y - this.lastY;
      if (dx * dx + dy * dy < MIN_SAMPLE_MOVE_SQ) return;
    }
    this.samples.push({ tMs, x, y });
    this.lastX = x;
    this.lastY = y;
  }

  /**
   * Force-append a final sample at `tMs` (the death / end frame), bypassing the
   * change-gate, so the recorded timeline's LENGTH equals the true run length. Without
   * this, a player who finds a safe pocket and holds still records no samples during
   * that stretch, so the server re-sim + replays (which run only up to the last
   * sample's `tMs`) would cut off early and under-score the run — a false anti-cheat
   * rejection. Holds the last target position (the player wasn't moving), so the
   * re-simulation reproduces the held-still survival exactly. No-op on an empty run or
   * if a sample already covers `tMs`.
   *
   * @param tMs the sim time the run ended at (the death frame's `elapsedMs`).
   */
  seal(tMs: number): void {
    if (this.samples.length === 0) return;
    const last = this.samples[this.samples.length - 1]!;
    if (tMs <= last.tMs) return;
    this.samples.push({ tMs, x: this.lastX, y: this.lastY });
  }

  /** How many input samples have been captured so far. */
  get length(): number {
    return this.samples.length;
  }

  /**
   * Produce the immutable {@link RunRecord} for the finished run, stamped with the
   * current {@link SIM_VERSION} so a stale record isn't misjudged after the sim
   * changes. The returned `samples` array is a defensive copy — recording may
   * continue (or the recorder be reset) without mutating an emitted record.
   */
  toRecord(): RunRecord {
    return {
      seed: this.seed,
      viewport: this.viewport,
      samples: this.samples.slice(),
      simVersion: SIM_VERSION,
      mode: this.mode,
    };
  }

  /** Drop all captured samples (e.g. on `scene.restart()` before a fresh run). */
  reset(): void {
    this.samples.length = 0;
    this.lastX = Number.NaN;
    this.lastY = Number.NaN;
  }
}
